// src/components/PanelDriver/PanelDriver.jsx
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { PanelNav } from "../Bootoverlay/Bootoverlay";
import AvatarPickerModal, { MiniHelmet } from "./Avatarpicker";
import {
  setField,
  setDriverClass,
  registerDriver,
  updateAvatar,
  resetForm,
  refreshCookieState,
  selectDriverForm,
  selectDriverStatus,
  selectDriverError,
  selectRegisteredDriver,
  selectGridCode,
  selectAvatar,
  selectCookieRegistered,
  selectCookieCallsign,
  selectCookieAvatar,
} from "../Paneldriver/DriverSlice/Driverslice";
import { formatGridCode } from "./DriverSlice/Gridcode";

const CLASS_MAP = { "": "—", zero: "ROOKIE", some: "DRIVER", vet: "VETERAN" };
const DEFAULT_AVATAR = { face: "neutral", visor: "none", suit: "carbon", skin: "medium", number: "" };
const DRIVER_CLASSES = [
  ["zero", "🏁", "ROOKIE", "Never driven a kart"],
  ["some", "⚡", "DRIVER", "Some track time"],
  ["vet", "🔥", "VETERAN", "Serious wheel time"],
];

const calculateAge = (dobString) => {
  if (!dobString) return "—";

  const today = new Date();
  const dob = new Date(dobString);

  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
};

export default function PanelDriver({ onNav, onRegistered }) {
  const dispatch = useDispatch();
  const form = useSelector(selectDriverForm);
  const status = useSelector(selectDriverStatus);
  const error = useSelector(selectDriverError);
  const gridCode = useSelector(selectGridCode);      // only set after fresh register
  const avatar = useSelector(selectAvatar);
  const cookieRegistered = useSelector(selectCookieRegistered);
  const cookieCallsign = useSelector(selectCookieCallsign);
  const cookieAvatar = useSelector(selectCookieAvatar);

  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch(refreshCookieState());
  }, [dispatch]);

  const loading = status === "loading";
  const registered = status === "succeeded";

  // Is the user currently typing into the form?
  const formHasData = !!(form.name || form.callsign || form.email || form.birthday || form.driverClass);

  // Card display values
  const displayCallsign = formHasData
    ? (form.callsign || "—")
    : (cookieRegistered ? (cookieCallsign || "—") : "—");

  const displayStatus = (registered || cookieRegistered) ? "REGISTERED" : "UNREGISTERED";
  const statusColor = (registered || cookieRegistered) ? "lime" : "";

  // Avatar: use cookie avatar for returning verified users when form is empty
  // Use current session avatar (default or customised) when form is active
  const displayAvatar = (!formHasData && cookieRegistered && cookieAvatar)
    ? cookieAvatar
    : avatar;

  // Grid code: ONLY show after a fresh successful registration in this session
  // NEVER show from cookie for returning users
  const showGridCode = registered && gridCode;

  const field = (name) => ({
    value: form[name],
    onChange: (e) => dispatch(setField({ field: name, value: e.target.value })),
  });

  const onCallsignChange = (e) =>
    dispatch(setField({
      field: "callsign",
      value: e.target.value.toUpperCase().replace(/[^A-Z0-9\s_-]/g, ""),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.email.trim()) { dispatch(setField({ field: "error", value: "Email address is required." })); return; }
    if (!form.termsChecked) { dispatch(setField({ field: "error", value: "Please acknowledge the terms to continue." })); return; }
    if (form.birthday) {
      const dob = new Date(form.birthday);
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 21);
      if (dob > cutoff) { dispatch(setField({ field: "error", value: "Driver must be at least 21 years old." })); return; }
    }
    dispatch(registerDriver({
      name: form.name, callsign: form.callsign, email: form.email,
      birthday: form.birthday, driverClass: form.driverClass, newsletterOptIn: form.newsletterOptIn,
    })).then((action) => {
      if (registerDriver.fulfilled.match(action)) onRegistered?.(form.callsign || form.email, form.email);
    });
  };

  const handleAvatarConfirm = (cfg) => { dispatch(updateAvatar(cfg)); setModalOpen(false); };

  return (
    <>
      {modalOpen && (
        <AvatarPickerModal value={displayAvatar} onConfirm={handleAvatarConfirm} onClose={() => setModalOpen(false)} />
      )}

      <section className="vk-panel" id="panel-driver">
        <div className="vk-driver-scroll">
          <div className="vk-driver-layout">
            <div>

              {/* Welcome back bar — only for verified returning users with empty form */}
              {cookieRegistered && !registered && !formHasData && (
                <div className="vk-cookie-status-bar">
                  <span className="vk-cookie-status-dot" />
                  <span>Welcome back, {cookieCallsign || "Driver"} — your slot is secured</span>
                </div>
              )}

              <div className="vk-driver-header">
                <div className="vk-section-eyebrow">
                  <span className="vk-eyebrow-num">05</span><span>//</span><span>DRIVER REGISTRY</span>
                </div>
                <h2 className="vk-section-title" style={{ borderBottom: "none" }}>
                  SIGN UP FOR <span className="orange">BROADCASTS</span>
                </h2>
                <p className="vk-section-sub">
                  Register your identity in the Vegas Karts system. Your mission briefing and pre-order receipt ship to your INBOX. Stay in the loop for Winter 2026 deployment.
                </p>
              </div>

              <div className="vk-driver-row">

                {/* ── LEFT FORM ── */}
                <div className="vk-driver-left">
                  {!registered ? (
                    <form className="vk-driver-form" onSubmit={handleSubmit} noValidate>
                      <div className="vk-form-name-row">
                        <div className="vk-form-group">
                          <label className="vk-form-label">NAME <span className="vk-form-optional">(OPTIONAL)</span></label>
                          <div className="vk-form-input-wrap">
                            <input className="vk-form-input" placeholder="John Doe" maxLength={40} autoComplete="given-name" {...field("name")} />
                          </div>
                        </div>
                        <div className="vk-form-group">
                          <label className="vk-form-label">CALLSIGN <span className="vk-form-optional">(OPTIONAL)</span></label>
                          <div className="vk-form-input-wrap">
                            <input className="vk-form-input" placeholder="e.g. GHOST, BLAZE, NITRO" maxLength={20} value={form.callsign} onChange={onCallsignChange} />
                          </div>
                        </div>
                      </div>
                      <div className="vk-form-name-row">
                        <div className="vk-form-group">
                          <label className="vk-form-label">EMAIL <span className="vk-form-req">*</span></label>
                          <div className="vk-form-input-wrap">
                            <input type="email" className="vk-form-input" placeholder="your@email.com" required autoComplete="email" {...field("email")} />
                          </div>
                        </div>
                        <div className="vk-form-group">
                          <label className="vk-form-label">DATE OF BIRTH <span className="vk-form-optional">(OPTIONAL — MUST BE 21+)</span></label>
                          <div className="vk-form-input-wrap">
                            <input type="date" className="vk-form-input vk-form-input--date"
                              max={new Date(new Date().setFullYear(new Date().getFullYear() - 21)).toISOString().split("T")[0]}
                              {...field("birthday")} />
                          </div>
                        </div>
                      </div>
                      <div className="vk-form-group">
                        <label className="vk-form-label">PRIOR LAPS <span className="vk-form-optional">(OPTIONAL)</span></label>
                        <div className="vk-laps-grid">
                          {DRIVER_CLASSES.map(([val, icon, title, sub]) => (
                            <button key={val} type="button"
                              className={`vk-laps-btn${form.driverClass === val ? " selected" : ""}`}
                              onClick={() => dispatch(setDriverClass(val))} aria-pressed={form.driverClass === val}>
                              <span className="vk-laps-icon">{icon}</span>
                              <span className="vk-laps-title">{title}</span>
                              <span className="vk-laps-sub">{sub}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="vk-form-legal">
                        <label className="vk-checkbox-label">
                          <input type="checkbox" required checked={form.termsChecked ?? false}
                            onChange={(e) => dispatch(setField({ field: "termsChecked", value: e.target.checked }))} />
                          <span className="vk-checkbox-custom" />
                          <span className="vk-legal-text">
                            I acknowledge I am 21+ and hold a valid driver's license. I agree to the <a href="/terms">Terms of Service</a> and acknowledge the <a href="/waiver">Liability Waiver</a> will be required before participation. <span className="vk-form-req">*</span>
                          </span>
                        </label>
                      </div>
                      <div className="vk-form-legal vk-form-newsletter">
                        <label className="vk-checkbox-label">
                          <input type="checkbox" checked={form.newsletterOptIn ?? false}
                            onChange={(e) => dispatch(setField({ field: "newsletterOptIn", value: e.target.checked }))} />
                          <span className="vk-checkbox-custom" />
                          <span className="vk-legal-text">Send me mission briefings, launch updates, and Winter 2026 deployment alerts.</span>
                        </label>
                      </div>
                      {error && <div className="vk-form-error"><span className="vk-form-error-icon">⚠</span> {error}</div>}
                      <button type="submit" className={`vk-btn-primary vk-btn-register${loading ? " loading" : ""}`}
                        disabled={loading || !form.termsChecked || !form.email.trim()}>
                        {loading ? "REGISTERING..." : "REGISTER DRIVER ▶"}
                      </button>
                      <p className="vk-form-hint">A verification link will be sent to your inbox. Click <strong>Confirm Driver Profile</strong> to activate your slot.</p>
                    </form>
                  ) : (
                    <div className="vk-driver-success">
                      <div className="vk-success-icon">✓</div>
                      <div className="vk-success-title">CHECK YOUR INBOX</div>
                      <div className="vk-success-callsign">// {form.callsign || form.email}</div>
                      <p className="vk-success-message">
                        A verification link has been sent to <span className="orange">{form.email}</span>.<br />
                        Click <strong>Confirm Driver Profile</strong> in the email to activate your slot.
                      </p>
                      {showGridCode && (
                        <div className="vk-success-gridcode">
                          <span className="vk-success-gridcode-label">GRID CODE</span>
                          <span className="vk-success-gridcode-val blue">{formatGridCode(gridCode)}</span>
                          <span className="vk-success-gridcode-hint">Your unique lobby identifier — use this at check-in</span>
                        </div>
                      )}
                      <button className="vk-btn-ghost" onClick={() => dispatch(resetForm())}>REGISTER ANOTHER ▶</button>
                    </div>
                  )}
                </div>

                {/* ── RIGHT CARD ── */}
                <div className="vk-driver-right">
                  <div className="vk-driver-card">
                    <div className="vk-dcp-scanline" />
                    <div className="vk-dcp-header">
                      <span className="vk-dcp-logo">VK</span>
                      <span className="vk-dcp-title">DRIVER PROFILE</span>
                    </div>

                    {/* Avatar — clickable, opens modal */}
                    <button type="button" className="vk-dcp-avatar-btn"
                      onClick={() => setModalOpen(true)}
                      title="Customize your driver avatar" aria-label="Customize avatar">
                      <div className="vk-dcp-avatar-inner">
                        <MiniHelmet avatar={displayAvatar} />
                      </div>
                      <span className="vk-dcp-avatar-edit-badge">✎ EDIT</span>
                    </button>

                    {form.name && <div className="vk-dcp-fullname">{form.name.toUpperCase()}</div>}
                    <div className="vk-dcp-callsign">{displayCallsign}</div>

                    <div className="vk-dcp-rows">
                      {[
                        ["STATUS", displayStatus, statusColor],
                        ["CLASS", CLASS_MAP[form.driverClass], ""],
                        ["AGE", calculateAge(form.birthday), ""],
                        ["DEPLOYMENT", "WINTER 2026", "blue"],
                      ].map(([label, val, color]) => (
                        <div key={label} className="vk-dcp-row">
                          <span className="vk-dcp-label">{label}</span>
                          <span className={`vk-dcp-val${color ? " " + color : ""}`}>{val}</span>
                        </div>
                      ))}
                      {/* Grid code — ONLY show after fresh registration, not from cookie */}
                      {showGridCode && (
                        <div className="vk-dcp-row">
                          <span className="vk-dcp-label">GRID CODE</span>
                          <span className="vk-dcp-val blue">{formatGridCode(gridCode)}</span>
                        </div>
                      )}
                    </div>

                    <div className="vk-dcp-footer">
                      <span>VEGAS KARTS LLC</span>
                      <span className="vk-dcp-serial blue">LAS VEGAS, NV</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
        <PanelNav prev={3} next={5} label="05 / 08 — REGISTRY" onNav={onNav} />
      </section>
    </>
  );
}